import React from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import {
  LinkButton,
  Modal,
  type ModalDetailItem,
} from '@enamelai/design-system'
import type { MockFileItem } from '@pages/Files/mockFilesData'
import {
  getFileExtension,
  getFileIconClass,
  getFileTypeFromFileName,
  type FileTypeKind,
} from '@components/pages/Files/filesAndLetters.util'
import { useAppSelector } from '@store/hooks'
import './FileDetailsModal.css'

const FILE_KIND_LABEL: Record<FileTypeKind, string> = {
  image: 'Image',
  pdf: 'PDF Document',
  dcm: 'DICOM Image',
  document: 'Word Document',
  other: 'File',
}

function formatDetailsDate(value?: string | null): string | null {
  if (!value) return null
  const date = new Date(value)
  if (isNaN(date.getTime())) return null
  return format(date, 'MMM d, yyyy')
}

export type FileDetailsFile = MockFileItem & {
  attachedToClaims?: boolean
  claimId?: string
  attachedToForms?: boolean
  uploadedBy?: string
}

export type FileDetailsModalProps = {
  show: boolean
  onHide: () => void
  file: FileDetailsFile | null
}

const FileDetailsModal: React.FC<FileDetailsModalProps> = ({
  show,
  onHide,
  file,
}) => {
  const navigate = useNavigate()
  const backendDetails = useAppSelector((state) => state.folder.fileDetails)

  if (!file) return null

  const displayName = backendDetails?.file_name || file.fileName || '—'
  const kind = getFileTypeFromFileName(displayName)
  const kindLabel =
    kind === 'other'
      ? `${getFileExtension(displayName).slice(1).toUpperCase()} File`.trim()
      : FILE_KIND_LABEL[kind]
  const displaySize =
    typeof backendDetails?.file_size === 'number'
      ? `${(backendDetails.file_size / 1024 / 1024).toFixed(2)} MB`
      : file.fileSize
  const attachedToClaims =
    backendDetails?.attached_to_claims ?? file.attachedToClaims ?? false
  const attachedToForms =
    backendDetails?.attached_to_forms ?? file.attachedToForms ?? false
  const claimId = backendDetails?.claim_ids?.[0] ?? file.claimId

  const details: ModalDetailItem[] = [
    {
      label: 'Date uploaded',
      value:
        formatDetailsDate(backendDetails?.upload_date) ??
        formatDetailsDate(file.uploadDate) ??
        '—',
    },
    {
      label: 'Uploaded by',
      value: backendDetails?.uploader ?? file.uploadedBy ?? '—',
    },
    {
      label: 'Attached to claims',
      value: attachedToClaims ? (
        <span className="file-details-claims">
          Yes
          {claimId && (
            <LinkButton
              size="sm"
              onClick={() => {
                navigate('/claims', { state: { claimId } })
                onHide()
              }}
            >
              View claim
            </LinkButton>
          )}
        </span>
      ) : (
        'No'
      ),
    },
    { label: 'Attached to forms', value: attachedToForms ? 'Yes' : 'No' },
  ]

  return (
    <Modal
      open={show}
      onClose={onHide}
      variant="fileDetails"
      title="File details"
      fileIcon={
        <i
          className={`mgc ${getFileIconClass(kind)} file-details-icon file-details-icon--${kind}`}
        />
      }
      fileName={displayName}
      fileMeta={[kindLabel, displaySize].filter(Boolean).join(' ')}
      details={details}
      primaryLabel=""
      secondaryLabel="Close"
      onSecondaryClick={onHide}
    />
  )
}

export default FileDetailsModal
